/**
 * La classe d'un enfant dans sa fiche.
 *
 * ## Pourquoi la demander ici
 *
 * Les journées pédagogiques (et bientôt le parascolaire) sont réservées aux
 * élèves d'une école, classe par classe : la base vérifie la classe à
 * l'inscription (`jpa_controler_inscription`). La page des journées la
 * demandait au dernier moment, enfant par enfant. La fiche la demande dès
 * qu'on sait qu'elle servira — quand l'école choisie est reliée à un centre
 * qui a des classes — et la redemande chaque année scolaire, parce qu'une
 * classe ne vaut que pour une année (`kid_classes`, une ligne par enfant et
 * par année).
 *
 * ## Obligatoire dans la fiche, pas pour les stages (VALIDÉ ÉCLOSION, 22/10/2026)
 *
 * La fiche ne s'enregistre pas sans classe quand l'école en a. Mais une
 * classe absente ne bloque que les journées pédagogiques et le parascolaire,
 * jamais un stage d'été : sinon chaque rentrée bloquerait des familles pour
 * une information qui ne concerne pas leur inscription.
 *
 * ## « Ma classe n'est pas dans la liste »
 *
 * Une liste incomplète ne doit pas bloquer un parent. Il nomme sa classe,
 * l'équipe en est prévenue (courriel de jpa@eclosion.be à info@eclosion.be)
 * et la fiche s'enregistre sans classe, avec la valeur `CLASSE_HORS_LISTE`
 * en mémoire du formulaire seulement — rien n'est écrit dans `kid_classes`,
 * et la classe reste « à indiquer » jusqu'à ce que l'équipe l'ait ajoutée et
 * que le parent la choisisse.
 *
 * Les textes restent généraux (« Classe en 2026-2027 ») : la classe servira
 * aussi au parascolaire.
 */

import type { ClasseDuCentre } from '../jpa/regles';
import { anneeScolaire, jourABruxelles, libelleAnneeScolaire } from '../jpa/regles';
import { normaliser } from '../catalogue';

export type { ClasseDuCentre };

/** La valeur, en mémoire du formulaire seulement, d'une classe signalée à l'équipe. */
export const CLASSE_HORS_LISTE = 'hors-liste';

export interface CentreRelie {
  id: string;
  school_id: string | null;
}

/** Ce que la fiche doit savoir des classes : les centres et leur école, les classes des centres. */
export interface ClassesConnues {
  centres: readonly CentreRelie[];
  classes: readonly ClasseDuCentre[];
}

const EST_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** L'année scolaire d'aujourd'hui (2026 pour 2026-2027) ; dès août, la suivante. */
export function anneeScolaireCourante(maintenant: Date = new Date()): number {
  return anneeScolaire(jourABruxelles(maintenant));
}

/**
 * Toutes les classes des centres reliés à cette école, actives ou non, dans
 * l'ordre d'affichage. Vide pour une école tapée à la main ou sans centre.
 */
export function classesDeLEcole(ecoleId: string, connues: ClassesConnues): ClasseDuCentre[] {
  const ecole = (ecoleId ?? '').trim();
  if (!EST_UUID.test(ecole)) return [];
  const centres = new Set(connues.centres.filter((c) => c.school_id === ecole).map((c) => c.id));
  return connues.classes
    .filter((c) => centres.has(c.centreId))
    .sort((a, b) => a.ordre - b.ordre || a.libelle.localeCompare(b.libelle, 'fr'));
}

/** Vrai si la fiche doit demander la classe : l'école a au moins une classe active. */
export function classeAChoisir(ecoleId: string, connues: ClassesConnues): boolean {
  return classesDeLEcole(ecoleId, connues).some((c) => c.active);
}

/**
 * Les options de la liste : les classes actives, et la classe actuelle de
 * l'enfant si elle a été fermée — on ne la propose plus, mais on l'affiche.
 */
export function optionsDeClasse(
  classes: readonly ClasseDuCentre[],
  classeActuelleId?: string | null,
): { valeur: string; libelle: string }[] {
  return classes
    .filter((c) => c.active || c.id === classeActuelleId)
    .map((c) => ({ valeur: c.id, libelle: c.active ? c.libelle : `${c.libelle} — classe fermée` }));
}

/**
 * Ce qui empêche d'enregistrer, pour la classe. Rien si l'école n'en demande
 * pas ; sinon une classe de cette école, ou la classe signalée hors liste.
 */
export function erreurClasse(ecoleId: string, classeId: string, connues: ClassesConnues | undefined): string | undefined {
  if (!connues || !classeAChoisir(ecoleId, connues)) return undefined;
  if (classeId === CLASSE_HORS_LISTE) return undefined;
  if (classesDeLEcole(ecoleId, connues).some((c) => c.id === classeId)) return undefined;
  return 'Choisissez sa classe, ou dites-nous si elle n’est pas dans la liste.';
}

/** « P1A — Madame Sophie · 2026-2027 ». */
export function libelleClasse(classe: Pick<ClasseDuCentre, 'libelle' | 'active'>, annee: number): string {
  return `${classe.libelle}${classe.active ? '' : ' (classe fermée)'} · ${libelleAnneeScolaire(annee)}`;
}

/**
 * Les écoles de la liste dont le nom ressemble à un nom tapé à la main :
 * sans l'identifiant, l'enfant n'a ni tarif local ni accès aux journées
 * pédagogiques. On propose, on n'impose pas.
 */
export function ecolesSemblables<E extends { id: string; name: string }>(
  nomTape: string,
  ecoles: readonly E[],
  max = 3,
): E[] {
  const mots = normaliser(nomTape)
    .split(' ')
    .filter((m) => m.length >= 4 && !['ecole', 'institut', 'college', 'athenee', 'saint', 'sainte', 'communale', 'libre'].includes(m));
  if (mots.length === 0) return [];
  return ecoles
    .map((e) => {
      const nom = ` ${normaliser(e.name)} `;
      const communs = mots.filter((m) => nom.includes(` ${m} `) || nom.includes(m)).length;
      return { e, communs };
    })
    .filter((x) => x.communs > 0)
    .sort((a, b) => b.communs - a.communs || a.e.name.localeCompare(b.e.name, 'fr'))
    .slice(0, max)
    .map((x) => x.e);
}

export type ResumeClasse =
  | { etat: 'choisie'; texte: string }
  | { etat: 'signalee'; texte: string }
  | { etat: 'a-indiquer'; texte: string };

/**
 * La classe telle que le récapitulatif et la fiche la disent, ou `null`
 * quand l'école n'en demande pas.
 */
export function resumeClasse(
  ecoleId: string,
  classeId: string,
  connues: ClassesConnues | undefined,
  annee: number,
): ResumeClasse | null {
  if (!connues || !classeAChoisir(ecoleId, connues)) return null;
  if (classeId === CLASSE_HORS_LISTE) {
    return { etat: 'signalee', texte: `Signalée à l’équipe · ${libelleAnneeScolaire(annee)}` };
  }
  const classe = classesDeLEcole(ecoleId, connues).find((c) => c.id === classeId);
  if (classe) return { etat: 'choisie', texte: libelleClasse(classe, annee) };
  return { etat: 'a-indiquer', texte: `À indiquer · ${libelleAnneeScolaire(annee)}` };
}
